import { useEffect, useState } from 'react';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import { getHigherAppPointSum } from '../utility';
import { useAppContext } from './AppContext';
import { ENVIZ_PN } from '../constant/prices';
import { useCurrency } from 'src/common/hooks/widgets/useCurrency';

export interface PropsFunc {
  appPoints: number;
  localeCode: string;
  currency: string;
  tileValue: string;
  country: string;
}

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

interface PriceParts {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: ScaleQtyPrice[];
}

interface ScaleQtyPrice {
  qty: number;
  price: number;
}
// "one discountFactor for all "
const partsToQuery = [
  {
    partNumber: ENVIZ_PN, // 50.-USD
  },
];

type MarketPlaceRequest = {
  currency: string;
  countryCode3: string;
  parts: typeof partsToQuery;
};

export const usePrice = ({
  appPoints,
  localeCode,
  currency,
  country,
}: PropsFunc) => {
  const { appState } = useAppContext();
  const [lowerPrice, setLowerPrice] = useCurrency(currency, localeCode, 0);
  const [higherPrice, setHigherPrice] = useCurrency(currency, localeCode, 0);
  const [dataFetched, setDataFetched] = useState([]);
  const [priceFile, setPriceFile] = useState({} as priceProps[]);
  const [loading, setLoading] = useState(false);
  const [price, setPrice] = useState(0);
  const [fetchingError, setFetchingError] = useState({});
  const totalHigherAppPoint = getHigherAppPointSum(appState);
  const [usPrice, setUsPrice] = useState(0);
  const [usLowerPrice, setUsLowerPrice] = useState(0);

  useEffect(() => {
    setLowerPrice(Math.round(appPoints) * price * 12);
    setUsLowerPrice(Math.round(appPoints) * usPrice * 12);
    setHigherPrice(Math.round(appPoints + totalHigherAppPoint) * price * 12);
  }, [appPoints, price, usPrice]);

  useEffect(() => {
    if (currency && dataFetched.length === 0) {
      fetchPrices();
      if (country !== 'USA') {
        fetchPrices('USD', 'USA');
      }
    }
  }, [localeCode, currency, country]);

  const isUndefinedForNumber = (value: number | undefined) => {
    return value ? value : 0;
  };

  const setUSPricesData = (products: priceProps[]) => {
    if (products?.length) {
      const Enviz_Product = products.find(
        (i: PriceParts) => i.partNumber == ENVIZ_PN,
      );
      const Enviz_Product_Price = Enviz_Product?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty === 100,
      )?.price;
      setUsPrice(isUndefinedForNumber(Enviz_Product_Price));
    }
  };

  const fetchPrices = (curr?: string, ctry?: string) => {
    setLoading(true);
    const body: MarketPlaceRequest = {
      currency: curr ? curr : currency,
      countryCode3: ctry ? ctry : country,
      parts: partsToQuery,
    };
    getListPrice(
      'https://api.marketplace.ibm.com/purchase/catalog/resources/price/info',
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          if (curr && ctry) {
            setUSPricesData(data.parts);
            setLoading(false);
          } else {
            setPriceFile(data.parts);
            setDataFetched(data.parts);
            setLoading(false);
          }
        } else {
          setDataFetched([]);
          setLoading(false);
        }
      })
      .catch((error) => {
        setDataFetched([]);
        setLoading(false);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };

  useEffect(() => {
    if (priceFile.length) {
      const priceArray = priceFile.map((item) => ({
        [item.partNumber]: item.scaleQtyPrice[0].price,
      }));
      const priceObj = Object.assign({}, ...priceArray);
      setPrice(priceObj.D09SJZX);
    }
  }, [priceFile]);

  return [lowerPrice, higherPrice, usLowerPrice, loading, fetchingError];
};

export default usePrice;
